'use client';
import { FC } from 'react';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';

import { cleanContentfulId } from '@telialt/utils/src/Contentful/cleanContentfulId';

import { LeadFormType } from '../../models/Content';
import { LeadErrorNotification } from './components/LeadErrorNotification';
import { LeadFormBlock } from './components/LeadFormBlock/LeadFormBlock';
import { LeadFormContent } from './components/LeadFormContent/LeadFormContent';
import { LeadFormModal } from './components/LeadFormModal/LeadFormModal';
import { LeadSuccessNotification } from './components/LeadSuccessNotification';

type Props = {
    contentItem: LeadFormType;
    modalUid?: string;
    showDefaultOpenModalButton?: boolean;
};

export const LeadForm: FC<Props> = ({ contentItem: initialContentItem, modalUid, showDefaultOpenModalButton }) => {
    const contentItem = useContentfulLiveUpdates(initialContentItem);
    const formType = contentItem.fields.formType;

    const leadSuccessNotification = <LeadSuccessNotification message={contentItem.fields.successMessage} />;
    const leadErrorNotification = <LeadErrorNotification />;
    const leadFormContent = (
        <LeadFormContent
            description={contentItem.fields.description}
            legalText={contentItem.fields.legalText?.fields?.description}
            commentHint={contentItem.fields.commentHint}
            productTypeValue={cleanContentfulId(contentItem.fields.productTypeValue)}
            fields={contentItem.fields.fields?.map((field) => cleanContentfulId(field))}
        />
    );

    if (formType === 'IN_PAGE') {
        return (
            <LeadFormBlock
                leadFormContent={leadFormContent}
                successNotification={leadSuccessNotification}
                errorNotification={leadErrorNotification}
                name={contentItem.fields.name}
                submitButtonLabel={contentItem.fields.submitButtonLabel}
                legalText={contentItem.fields.legalText}
                commentHint={contentItem.fields.commentHint}
            />
        );
    }

    if (formType === 'MODAL') {
        return (
            <LeadFormModal
                leadFormContent={leadFormContent}
                successNotification={leadSuccessNotification}
                errorNotification={leadErrorNotification}
                name={contentItem.fields.name}
                submitButtonLabel={contentItem.fields.submitButtonLabel}
                legalText={contentItem.fields.legalText}
                commentHint={contentItem.fields.commentHint}
                uid={modalUid || contentItem.sys.id}
                showDefaultOpenModalButton={showDefaultOpenModalButton}
            />
        );
    }

    return null;
};
